import { yupResolver } from '@hookform/resolvers/yup';
import { Coupon, CouponType } from '@thurstech/commons/dist/entity/coupon';
import { useForm } from 'react-hook-form';

import Button from '../../components/button/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/input/Input';
import InputDate from '../../components/input/InputDate';
import InputSelect from '../../components/input/InputSelect';
import InputTextArea from '../../components/input/InputTextArea';
import { useAppDispatch, useAppSelector } from '../../hooks/store';
import { isThunkLoading } from '../../redux/common';
import { addCouponThunk, updateCouponThunk } from '../../redux/reducer/propSlice';
import { withErrorHandler } from '../../utils/utils';
import { AddCouponFormData, addCouponSchema } from '../../validation/operations/coupon';

interface ModalProps {
  coupon?: Coupon;
  isOpen: boolean;
  onClose: () => void;
}
export const CouponModal: React.FC<ModalProps> = ({ coupon, isOpen, onClose }) => {
  let values: AddCouponFormData = {} as AddCouponFormData;
  if (coupon) {
    values = {
      id: coupon.id,
      code: coupon.code,
      expiry: coupon.expiry,
      type: coupon.type,
      description: coupon.description,
      minimumOrder: coupon.minimumOrder
    };
    if (coupon.type === CouponType.FIXED) {
      values.discount = coupon.discount;
    } else {
      values.percent = coupon.percent;
      values.maxDiscount = coupon.maxDiscount;
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(addCouponSchema),
    values,
    defaultValues: { type: CouponType.FIXED }
  });

  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(state => state.prop.isLoading);
  const onSubmit = (data: AddCouponFormData) =>
    withErrorHandler(async () => {
      if (!data.id) {
        await dispatch(addCouponThunk(data)).unwrap();
      } else {
        await dispatch(updateCouponThunk(data)).unwrap();
      }
      onClose();
    });

  const commonProps = {
    register,
    errors,
    topLabel: true
  };
  const type = watch('type');
  return (
    <Modal isOpen={isOpen} onClose={onClose} header="Coupon Detail">
      <form className="w-2/3" onSubmit={handleSubmit(onSubmit)}>
        <Input {...commonProps} readOnly hidden name="id" type="text" />
        <Input
          {...commonProps}
          name="code"
          type="text"
          label="Coupon Code"
          required
          readOnly={!!coupon}
          placeholder="Please enter coupon code"
        />
        <InputTextArea
          {...commonProps}
          name="description"
          label="Description"
          placeholder="Please enter Description"
        />
        <Input
          {...commonProps}
          name="minimumOrder"
          type="numer"
          label="Minimum Order Amount"
          placeholder="Please enter Minimum Order Amount"
        />
        <InputDate
          {...commonProps}
          required
          showTimeSelect
          watch={watch}
          setValue={setValue}
          name="expiry"
          label="Expiry Date"
          placeholder="Please enter Expiry Date"
        />
        <InputSelect
          {...commonProps}
          required
          name="type"
          label="Coupon Type"
          options={[
            { id: CouponType.FIXED, value: 'Fixed Discount Coupon' },
            { id: CouponType.PERCENT, value: 'Order Amount Percentage Discount Coupon' }
          ].filter(val => !coupon || val.id === coupon.type)}
          placeholder="Please Select Coupon Type"
        />
        {type === CouponType.FIXED ? (
          <Input
            {...commonProps}
            required
            name="discount"
            type="numer"
            label="Discount"
            placeholder="Please enter Discount Amount"
          />
        ) : (
          <>
            <Input
              {...commonProps}
              required
              name="percent"
              type="numer"
              label="Discount Percentage"
              placeholder="Please enter Discount Percentage"
            />
            <Input
              {...commonProps}
              name="maxDiscount"
              type="numer"
              label="Maximum Discount"
              placeholder="Please enter Maximum Discount Amount"
            />
          </>
        )}
        <Button
          label="Save Coupon"
          type="submit"
          className="font-medium bg-[#35e0a1] py-2 px-4 mt-6 rounded-md"
          isLoading={isThunkLoading(isLoading, addCouponThunk, updateCouponThunk)}
        />
      </form>
    </Modal>
  );
};
